import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Plus, Users, Hash, Megaphone, MessageSquare,
  Pin, Settings, Loader2, ArrowLeft, X
} from 'lucide-react';
import { channelApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const POLL_INTERVAL = 3000;

function Community() {
  const { user } = useAuthStore();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const [lastPollTime, setLastPollTime] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [newChannel, setNewChannel] = useState({
    name: '',
    description: '',
    channelType: 'broadcast'
  });

  const isTrainerOrAdmin = user?.role === 'trainer' || user?.role === 'admin';

  useEffect(() => {
    loadChannels();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (activeChannel) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [activeChannel]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    setLastPollTime(new Date().toISOString());

    pollIntervalRef.current = setInterval(async () => {
      if (!lastPollTime || !activeChannel) return;

      try {
        const response = await channelApi.poll(activeChannel.id, lastPollTime);
        const { messages: newMessages, timestamp } = response.data;

        if (newMessages.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const filtered = newMessages.filter(m => !existingIds.has(m.id));
            return [...prev, ...filtered];
          });
          loadChannels();
        }

        setLastPollTime(timestamp);
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, POLL_INTERVAL);
  }, [lastPollTime, activeChannel]);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const loadChannels = async () => {
    try {
      const response = await channelApi.getChannels();
      setChannels(response.data);
    } catch (error) {
      toast.error('Failed to load channels');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChannel = async (channel) => {
    try {
      setActiveChannel(channel);
      const response = await channelApi.getChannel(channel.id);
      setMessages(response.data.messages);
      setPinnedMessages(response.data.pinnedMessages);
      setCanPost(response.data.canPost);
      setLastPollTime(new Date().toISOString());

      // Mark as read
      await channelApi.markAsRead(channel.id);
      loadChannels();
    } catch (error) {
      toast.error('Failed to load channel');
    }
  };

  const createChannel = async (e) => {
    e.preventDefault();
    if (!newChannel.name.trim()) return;

    try {
      await channelApi.createChannel(
        newChannel.name,
        newChannel.description,
        newChannel.channelType
      );
      toast.success('Channel created');
      setShowCreateModal(false);
      setNewChannel({ name: '', description: '', channelType: 'broadcast' });
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create channel');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeChannel || isSending) return;

    const content = input.trim();
    setInput('');
    setIsSending(true);

    try {
      const response = await channelApi.postMessage(activeChannel.id, content);
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const togglePin = async (messageId, currentlyPinned) => {
    try {
      await channelApi.pinMessage(activeChannel.id, messageId, !currentlyPinned);
      loadChannel(activeChannel);
      toast.success(currentlyPinned ? 'Message unpinned' : 'Message pinned');
    } catch (error) {
      toast.error('Failed to update pin');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Channel List */}
      <div className={`w-full md:w-72 border-r bg-white flex flex-col ${activeChannel ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">Community</h1>
            {isTrainerOrAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                title="Create Channel"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {channels.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No channels available</p>
            </div>
          ) : (
            <div className="space-y-1">
              {channels.map(channel => (
                <button
                  key={channel.id}
                  onClick={() => loadChannel(channel)}
                  className={`w-full p-3 text-left rounded-lg transition-colors ${
                    activeChannel?.id === channel.id
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    {channel.channelType === 'broadcast' ? (
                      <Megaphone className="w-5 h-5 mr-2 text-gray-400" />
                    ) : (
                      <Hash className="w-5 h-5 mr-2 text-gray-400" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{channel.name}</p>
                        {channel.unreadCount > 0 && (
                          <span className="ml-2 bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                            {channel.unreadCount}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {channel.memberCount} members
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Channel Content */}
      <div className={`flex-1 flex flex-col bg-gray-50 ${!activeChannel ? 'hidden md:flex' : 'flex'}`}>
        {activeChannel ? (
          <>
            {/* Channel Header */}
            <div className="p-4 border-b bg-white flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => setActiveChannel(null)}
                  className="md:hidden p-2 mr-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                {activeChannel.channelType === 'broadcast' ? (
                  <Megaphone className="w-5 h-5 mr-2 text-gray-500" />
                ) : (
                  <Hash className="w-5 h-5 mr-2 text-gray-500" />
                )}
                <div>
                  <p className="font-semibold">{activeChannel.name}</p>
                  <p className="text-xs text-gray-500">
                    {activeChannel.channelType === 'broadcast' ? 'Broadcast' : 'Discussion'} ·{' '}
                    {activeChannel.trainerName}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {pinnedMessages.length > 0 && (
                  <button
                    onClick={() => setShowPinned(!showPinned)}
                    className={`p-2 rounded-lg transition-colors ${
                      showPinned ? 'bg-primary-100 text-primary-600' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    title="Pinned Messages"
                  >
                    <Pin className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Pinned Messages Panel */}
            {showPinned && pinnedMessages.length > 0 && (
              <div className="p-4 bg-amber-50 border-b">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-amber-800">
                    <Pin className="w-4 h-4 inline mr-1" />
                    Pinned Messages
                  </p>
                  <button
                    onClick={() => setShowPinned(false)}
                    className="text-amber-600 hover:text-amber-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {pinnedMessages.map(msg => (
                    <div key={msg.id} className="bg-white rounded-lg p-3 text-sm">
                      <p className="font-medium text-gray-700">{msg.senderName}</p>
                      <p className="text-gray-600 truncate">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No messages yet</p>
                  {canPost && <p className="text-sm mt-1">Be the first to post!</p>}
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const showDate = idx === 0 ||
                    formatDate(messages[idx - 1].createdAt) !== formatDate(msg.createdAt);

                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex items-center justify-center my-4">
                          <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {formatDate(msg.createdAt)}
                          </span>
                        </div>
                      )}
                      <div className={`group flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] ${msg.isOwn ? 'order-2' : ''}`}>
                          <div className="flex items-center mb-1">
                            {!msg.isOwn && (
                              <span className="text-sm font-medium text-gray-700 mr-2">
                                {msg.senderName}
                              </span>
                            )}
                            <span className="text-xs text-gray-400">{formatTime(msg.createdAt)}</span>
                            {!!msg.isPinned && (
                              <Pin className="w-3 h-3 ml-2 text-amber-500" />
                            )}
                          </div>
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              msg.isOwn
                                ? 'bg-primary-600 text-white'
                                : msg.messageType === 'announcement'
                                  ? 'bg-amber-100 border border-amber-200'
                                  : 'bg-white border border-gray-200'
                            }`}
                          >
                            {msg.messageType === 'announcement' && (
                              <div className="flex items-center text-amber-700 text-xs mb-1">
                                <Megaphone className="w-3 h-3 mr-1" />
                                Announcement
                              </div>
                            )}
                            <div className={`prose prose-sm max-w-none ${msg.isOwn ? 'prose-invert' : ''}`}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                            </div>
                          </div>
                          {/* Pin action for trainer/admin */}
                          {isTrainerOrAdmin && activeChannel.trainerId === user.id && (
                            <button
                              onClick={() => togglePin(msg.id, msg.isPinned)}
                              className="opacity-0 group-hover:opacity-100 mt-1 text-xs text-gray-500 hover:text-primary-600 transition-opacity"
                            >
                              {msg.isPinned ? 'Unpin' : 'Pin'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {canPost ? (
              <form onSubmit={sendMessage} className="p-4 border-t bg-white">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    disabled={isSending || !input.trim()}
                    className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-4 border-t bg-gray-100 text-center text-gray-500 text-sm">
                <Megaphone className="w-5 h-5 inline mr-2" />
                This is a broadcast channel. Only the trainer can post.
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a channel</p>
              <p className="text-sm mt-1">to view messages</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Channel Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Create Channel</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={createChannel} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={newChannel.name}
                  onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Memory Palace Tips"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newChannel.description}
                  onChange={(e) => setNewChannel({ ...newChannel, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="What's this channel about?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="channelType"
                      value="broadcast"
                      checked={newChannel.channelType === 'broadcast'}
                      onChange={(e) => setNewChannel({ ...newChannel, channelType: e.target.value })}
                      className="mt-1"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Megaphone className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Broadcast</span>
                      </div>
                      <p className="text-sm text-gray-500">Only you can post, students can read</p>
                    </div>
                  </label>
                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="channelType"
                      value="discussion"
                      checked={newChannel.channelType === 'discussion'}
                      onChange={(e) => setNewChannel({ ...newChannel, channelType: e.target.value })}
                      className="mt-1"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Hash className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Discussion</span>
                      </div>
                      <p className="text-sm text-gray-500">Everyone can post and discuss</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Channel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Community;
